<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, reactive, nextTick, toRef } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { staticName } from '@/lib/asset';
import ImageLazy from '@/components/ImageLazy.vue';
import { Amplitude } from '@seede/amplitude';
import { i18n, t } from '~/i18n';

const props = withDefaults(
  defineProps<{
    /** Docs / learn pages: carousel only, smaller cards */
    embedded?: boolean;
  }>(),
  { embedded: false }
);

const { isAuthenticated } = useAuth();
const locale = toRef(i18n.options, 'locale');
const pagePath = computed(() => (import.meta.client ? window.location.href : ''));
const isJapanese = computed(() => locale.value === 'ja');

const ctaHeaderLabel = computed(() => {
  void locale.value;
  return t('landing_home.every_scene.cta_header');
});

const ctaCardDesignMoreLines = computed(() => {
  void locale.value;
  return t('landing_home.every_scene.cta_card.design_more').split('\n');
});

const ctaCardContentInOutLines = computed(() => {
  void locale.value;
  return t('landing_home.every_scene.cta_card.content_in_out').split('\n');
});

const ctaCardLinkParts = computed(() => {
  void locale.value;
  const link = t('landing_home.every_scene.cta_card.link');
  const spaceIndex = link.indexOf(' ');
  if (spaceIndex === -1) return { left: link, right: '' };
  return { left: link.slice(0, spaceIndex), right: link.slice(spaceIndex + 1) };
});

const carouselRef = ref<HTMLElement | null>(null);
let autoScrollInterval: ReturnType<typeof setInterval> | null = null;

const startAutoScroll = () => {
  autoScrollInterval = setInterval(() => {
    if (carouselRef.value) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.value;
      if (scrollLeft + clientWidth >= scrollWidth - 10) {
        stopAutoScroll();
      } else {
        carouselRef.value.scrollBy({ left: 1, behavior: 'auto' });
      }
    }
  }, 30);
};

const stopAutoScroll = () => {
  if (autoScrollInterval) {
    clearInterval(autoScrollInterval);
    autoScrollInterval = null;
  }
};

onMounted(() => {
  startAutoScroll();
});

onUnmounted(() => {
  stopAutoScroll();
});

const imageOverflows = reactive<Record<string, boolean>>({});

const checkImageOverflow = (sceneId: string, event: Event) => {
  const img = event.target as HTMLImageElement;
  const container = img.parentElement;
  if (!container) return;
  nextTick(() => {
    const containerRatio = container.clientHeight / container.clientWidth;
    const imageRatio = img.naturalHeight / img.naturalWidth;
    imageOverflows[sceneId] = imageRatio > containerRatio + 0.01;
  });
};

const sceneConfigs = [
  {
    id: 'ecommerce',
    sceneKey: 'ecommerce',
    image: `${staticName}/static-assets/EveryScene/ecommerce.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(255, 107, 53, 0.35) 0%, transparent 70%)',
    bgColor: '#E8C9C0',
  },
  {
    id: 'edm',
    sceneKey: 'edm',
    image: `${staticName}/static-assets/EveryScene/EDM.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(147, 51, 234, 0.35) 0%, transparent 70%)',
    bgColor: '#D4C8E0',
  },
  {
    id: 'hiring',
    sceneKey: 'hiring',
    image: `${staticName}/static-assets/EveryScene/HiringPost.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(59, 130, 246, 0.35) 0%, transparent 70%)',
    bgColor: '#C0D4D8',
  },
  {
    id: 'travel',
    sceneKey: 'travel',
    image: `${staticName}/static-assets/EveryScene/TravelRoadBook.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(34, 197, 94, 0.35) 0%, transparent 70%)',
    bgColor: '#C4D4BC',
  },
  {
    id: 'academic',
    sceneKey: 'academic',
    image: `${staticName}/static-assets/EveryScene/Academic.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(234, 179, 8, 0.35) 0%, transparent 70%)',
    bgColor: '#EED8A0',
  },
  {
    id: 'restaurant',
    sceneKey: 'restaurant',
    image: `${staticName}/static-assets/card-bg/9-16/art.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(239, 68, 68, 0.35) 0%, transparent 70%)',
    bgColor: '#E8C0B8',
  },
  {
    id: 'event-promo',
    sceneKey: 'event_promo',
    image: `${staticName}/static-assets/card-bg/9-16/EventPost02.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(249, 115, 22, 0.35) 0%, transparent 70%)',
    bgColor: '#DEC898',
  },
  {
    id: 'brand',
    sceneKey: 'brand',
    image: `${staticName}/static-assets/card-bg/9-16/nike1.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(14, 165, 233, 0.35) 0%, transparent 70%)',
    bgColor: '#B8CCD4',
  },
  {
    id: 'product-launch',
    sceneKey: 'product_launch',
    image: `${staticName}/static-assets/card-bg/9-16/ProductPost.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(132, 204, 22, 0.35) 0%, transparent 70%)',
    bgColor: '#C8D8A4',
  },
  {
    id: 'infographic',
    sceneKey: 'infographic',
    image: `${staticName}/static-assets/card-bg/9-16/InfoGraphic.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(251, 191, 36, 0.35) 0%, transparent 70%)',
    bgColor: '#EECFA0',
  },
  {
    id: 'campaign',
    sceneKey: 'campaign',
    image: `${staticName}/static-assets/card-bg/9-16/EventPost.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(219, 39, 119, 0.35) 0%, transparent 70%)',
    bgColor: '#E8B8C8',
  },
];

const horizontalCardConfigs = [
  {
    id: 'presentation',
    sceneKey: 'presentation',
    image: `${staticName}/static-assets/card-bg/9-16/Presentation.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(99, 102, 241, 0.35) 0%, transparent 70%)',
    bgColor: '#BCC0D8',
  },
  {
    id: 'marketing-deck',
    sceneKey: 'marketing_deck',
    image: `${staticName}/static-assets/card-bg/9-16/MarketingDeck.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(236, 72, 153, 0.35) 0%, transparent 70%)',
    bgColor: '#D0C0D4',
  },
  {
    id: 'deck',
    sceneKey: 'pitch_deck',
    image: `${staticName}/static-assets/card-bg/9-16/Deck.webp`,
    glow: 'radial-gradient(ellipse 80% 30% at 50% 100%, rgba(34, 197, 94, 0.35) 0%, transparent 70%)',
    bgColor: '#B8D0C0',
  },
];

const scenes = computed(() => {
  void locale.value;
  return sceneConfigs.map((scene) => ({
    ...scene,
    label: t(`landing_home.every_scene.scene.${scene.sceneKey}`),
  }));
});

const horizontalCards = computed(() => {
  void locale.value;
  return horizontalCardConfigs.map((card) => ({
    ...card,
    label: t(`landing_home.every_scene.scene.${card.sceneKey}`),
  }));
});

const trackDesignNow = (target: string, source: string) => {
  Amplitude.event('nav_link_click', {
    link_text: ctaHeaderLabel.value,
    link_target: target,
    section: props.embedded ? 'learn_intro_carousel' : 'features_every_scene',
    source,
    auth_state: isAuthenticated.value ? 'authenticated' : 'anonymous',
    page_path: pagePath.value,
  });
};

const carouselClass = computed(() =>
  props.embedded
    ? 'flex gap-2 sm:gap-3 overflow-x-auto p-2 scrollbar-hide'
    : 'flex gap-4 overflow-x-auto p-4 scrollbar-hide'
);

const sceneCardClass = computed(() =>
  props.embedded
    ? 'flex-shrink-0 w-[140px] sm:w-[180px] md:w-[220px] overflow-hidden group p-1.5 rounded-xl relative'
    : 'flex-shrink-0 w-[240px] md:w-[320px] overflow-hidden group p-2 rounded-2xl relative'
);

const sceneLabelClass = computed(() =>
  props.embedded
    ? 'block text-xs md:text-sm font-boldonse text-black uppercase'
    : 'block text-base md:text-xl font-boldonse text-black uppercase'
);

const horizontalCardHClass = computed(() =>
  props.embedded
    ? 'h-[calc((140px-0.75rem)*16/9)] sm:h-[calc((180px-0.75rem)*16/9)] md:h-[calc((220px-0.75rem)*16/9)] rounded-md overflow-hidden relative z-10 flex items-start'
    : 'h-[calc((240px-1rem)*16/9)] md:h-[calc((320px-1rem)*16/9)] rounded-lg overflow-hidden relative z-10 flex items-start'
);

const horizontalCardWrapClass = computed(() =>
  props.embedded
    ? 'flex-shrink-0 overflow-hidden p-1.5 rounded-xl relative'
    : 'flex-shrink-0 overflow-hidden p-2 rounded-2xl relative'
);

const cardFooterClass = computed(() =>
  props.embedded ? 'px-1.5 pt-2.5 pb-1.5 relative z-10' : 'px-2 pt-4 pb-2 relative z-10'
);
</script>

<template>
  <section id="features" :class="embedded ? 'min-w-0 overflow-hidden' : 'py-16 md:py-40'">
    <div v-if="!embedded" class="px-4 md:px-8 mb-6 md:mb-12 text-center">
      <p class="text-xl md:text-2xl text-black/80 font-semibold py-4 uppercase">
        {{ $t('landing_home.every_scene.eyebrow') }}
      </p>
      <h2
        class="font-boldonse text-[clamp(1.5rem,4vw,2.5rem)] text-black/90 uppercase leading-[1.6] landing-title-multiline landing-title-section-xl"
      >
        <span class="block landing-title-line">{{
          $t('landing_home.every_scene.heading_line1')
        }}</span>
        <span class="block landing-title-line">{{
          $t('landing_home.every_scene.heading_line2')
        }}</span>
      </h2>
      <div class="flex justify-center mt-6 md:mt-8">
        <NuxtLink
          :to="isAuthenticated ? '/workspace/create' : '/signup'"
          class="px-5 py-2.5 rounded-full bg-black/90 text-white text-sm font-medium hover:bg-black/80 transition-colors inline-block"
          @click="
            trackDesignNow(isAuthenticated ? '/workspace/create' : '/signup', 'section_header')
          "
        >
          {{ ctaHeaderLabel }}
        </NuxtLink>
      </div>
    </div>

    <div
      ref="carouselRef"
      :class="carouselClass"
      @mouseenter="stopAutoScroll"
      @mouseleave="startAutoScroll"
    >
      <div
        v-for="scene in scenes"
        :key="scene.id"
        :class="sceneCardClass"
        :style="{ backgroundColor: scene.bgColor }"
      >
        <ImageLazy
          :src="scene.image"
          :alt="scene.label"
          :class="
            embedded
              ? 'aspect-[9/16] rounded-md overflow-hidden relative z-10'
              : 'aspect-[9/16] rounded-lg overflow-hidden relative z-10'
          "
          :image-class="
            [
              'absolute w-full h-auto top-0 pointer-events-none',
              imageOverflows[scene.id]
                ? 'group-hover:top-full group-hover:-translate-y-full transition-all duration-[10000ms] linear'
                : '',
            ].join(' ')
          "
          @load="checkImageOverflow(scene.id, $event)"
        />
        <div :class="cardFooterClass">
          <span :class="sceneLabelClass">{{ scene.label }}</span>
        </div>
      </div>

      <!-- Horizontal cards -->
      <div
        v-for="card in horizontalCards"
        :key="card.id"
        :class="horizontalCardWrapClass"
        :style="{ backgroundColor: card.bgColor }"
      >
        <ImageLazy
          :src="card.image"
          :alt="card.label"
          :class="horizontalCardHClass"
          :image-class="
            embedded ? 'max-h-full w-auto max-w-[420px]' : 'max-h-full w-auto max-w-[640px]'
          "
        />
        <div :class="cardFooterClass">
          <span :class="sceneLabelClass">{{ card.label }}</span>
        </div>
      </div>

      <!-- CTA Card -->
      <div
        :class="
          embedded
            ? 'bg-black/90 flex-shrink-0 w-[140px] sm:w-[180px] md:w-[220px] overflow-hidden group p-1.5 rounded-xl relative flex flex-col'
            : 'bg-black/90 flex-shrink-0 w-[240px] md:w-[320px] overflow-hidden group p-2 rounded-2xl relative flex flex-col'
        "
      >
        <div
          :class="
            embedded
              ? 'aspect-[9/16] overflow-hidden relative z-10 flex flex-col p-2'
              : 'aspect-[9/16] overflow-hidden relative z-10 flex flex-col p-2.5'
          "
        >
          <!-- Top section -->
          <div class="flex-1 relative">
            <span
              :class="
                isJapanese
                  ? 'absolute top-0 left-0 text-white text-[1.9rem] md:text-[2.5rem] uppercase tracking-tight leading-[0.95] whitespace-nowrap'
                  : embedded
                    ? 'absolute top-0 left-0 text-white text-sm sm:text-lg md:text-xl uppercase tracking-tight leading-[0.9]'
                    : 'absolute top-0 left-0 text-white text-[2rem] md:text-[3rem] uppercase tracking-tight leading-[0.9]'
              "
            >
              <template
                v-for="(line, index) in ctaCardDesignMoreLines"
                :key="`design-more-${index}`"
              >
                {{ line }}<br v-if="index < ctaCardDesignMoreLines.length - 1" />
              </template>
            </span>
            <span
              :class="
                isJapanese
                  ? 'absolute bottom-2 right-0 text-white text-[1.2rem] md:text-[1.6rem] leading-[1.15] text-right whitespace-nowrap'
                  : embedded
                    ? 'absolute bottom-0 right-0 text-white text-xs md:text-sm uppercase tracking-wider leading-[1]'
                    : 'absolute bottom-0 right-0 text-white leading-[1] text-right'
              "
            >
              <template
                v-for="(line, index) in ctaCardContentInOutLines"
                :key="`content-in-out-${index}`"
              >
                {{ line }}<br v-if="index < ctaCardContentInOutLines.length - 1" />
              </template>
            </span>
          </div>
          <!-- Bottom section -->
          <div class="flex-1 relative">
            <div
              :class="
                isJapanese
                  ? 'absolute bottom-0 left-0 text-white text-[1.75rem] md:text-[2.4rem] uppercase tracking-tight leading-[1.0] whitespace-nowrap'
                  : embedded
                    ? 'absolute bottom-0 left-0 text-white text-sm sm:text-lg md:text-xl uppercase tracking-tight leading-[0.9]'
                    : 'absolute bottom-0 left-0 text-white text-[2rem] md:text-[3rem] uppercase tracking-tight leading-[0.9]'
              "
            >
              <template v-if="isJapanese">
                <span class="block">{{
                  $t('landing_home.every_scene.cta_card.with_veeso_now_line1')
                }}</span>
                <span class="block ml-6"
                  >{{ $t('landing_home.every_scene.cta_card.with_veeso_now_line2')
                  }}{{ $t('landing_home.every_scene.cta_card.with_veeso_now_line3') }}</span
                >
              </template>
              <template v-else>
                <span class="block">{{
                  $t('landing_home.every_scene.cta_card.with_veeso_now_line1')
                }}</span>
                <span :class="embedded ? 'block ml-5 sm:ml-8' : 'block ml-12'">{{
                  $t('landing_home.every_scene.cta_card.with_veeso_now_line2')
                }}</span>
                <span class="block">{{
                  $t('landing_home.every_scene.cta_card.with_veeso_now_line3')
                }}</span>
              </template>
            </div>
          </div>
        </div>
        <div :class="cardFooterClass">
          <a
            href="https://seede.design"
            :class="[
              'flex justify-between w-full text-white font-boldonse uppercase transition-colors',
              embedded
                ? 'text-xs md:text-sm'
                : isJapanese
                  ? 'text-lg md:text-2xl'
                  : 'text-base md:text-lg',
            ]"
            @click="trackDesignNow('https://seede.design', 'carousel_cta_card')"
          >
            <span>{{ ctaCardLinkParts.left }}</span
            ><span>{{ ctaCardLinkParts.right }}</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
